import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { dateYYYYMMDD, qualifier } from "$root/utils";
import { Dialog, DialogFooter } from "@mdotm/shadcn/legacy/components";
import { DatePickerInput, Button, type FormSubmitOpts, FormField, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { adaptAnimatedNodeProvider, focusOnMount, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

export type DatePickerDialogCommon = {
	/** Dialog title. */
	header: ReactNode;
	/** Label of the confirm button. @default t("BUTTON.SAVE") */
	confirmLabel?: ReactNode;
	/** Pre-selected date (same shape as {@link DatePickerInput}'s value). */
	defaultValue?: string | null;
	/** Earliest selectable date (inclusive, ISO string). */
	minDate?: string;
	/** Latest selectable date (inclusive, ISO string). @default new Date().toISOString() */
	maxDate?: string;
	/** Optional content rendered just above the date picker (e.g. a description). */
	children?: ReactNode;
	/** Invoked with the selected date when the user confirms. The confirm button tracks its async state. */
	// TODO(MDOT-24848): same as `_base-dialog`'s prop — `opts` reaches no caller, and the shape is
	// written out because `FormSubmitFn` fixes its first parameter to a `FormEvent`.
	onSubmitAsync?: (date: string, opts: FormSubmitOpts) => MaybePromise<void>;
};

export type DatePickerDialogProps = DatePickerDialogCommon & MinimumDialogProps;

export function DatePickerDialog(props: DatePickerDialogProps): React.JSX.Element {
	const {
		show,
		onClose,
		onAnimationStateChange,
		onSubmitAsync,
		defaultValue,
		minDate,
		maxDate,
		header,
		confirmLabel,
		children,
	} = props;
	const { t } = useTranslation();

	const [date, setDate] = useState<string | null>(defaultValue ?? null);

	return (
		<Dialog
			show={show}
			header={header}
			onAnimationStateChange={onAnimationStateChange}
			onClose={onClose}
			onSubmitAsync={async (_event, opts) => {
				if (date == null) {
					return;
				}
				await onSubmitAsync?.(date, opts);
			}}
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton
							size="small"
							disabled={date == null}
							data-qualifier={qualifier.component.button("DatePickerDialog/Confirm")}
						>
							{confirmLabel ?? t("BUTTON.SAVE")}
						</SubmitButton>
					}
					neutralAction={
						<Button
							size="small"
							palette="tertiary"
							type="button"
							onClick={() => onClose?.()}
							data-qualifier={qualifier.component.button("DatePickerDialog/Cancel")}
						>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			{children}
			<FormField label="Date">
				{(formFieldProps) => (
					<DatePickerInput
						{...formFieldProps}
						innerRef={focusOnMount}
						value={date ?? ""}
						min={minDate}
						max={maxDate ?? dateYYYYMMDD(new Date())}
						onChange={(newDate) => setDate(newDate || null)}
						data-qualifier="DatePickerDialog/Date"
					/>
				)}
			</FormField>
		</Dialog>
	);
}

export type SpawnDatePickerDialogParams = DatePickerDialogCommon;

/**
 * Spawns a dialog letting the user pick a date.
 *
 * Resolves with the selected {@link DatePickerInput} value when confirmed, or
 * `null` if the dialog is dismissed without confirming.
 *
 * An optional {@link DatePickerDialogCommon["onSubmitAsync"]} runs while the confirm
 * button shows its loading state, before the promise resolves.
 */
export function spawnDatePickerDialog(params: SpawnDatePickerDialogParams): Promise<string | null> {
	const { onSubmitAsync, ...forward } = params;

	return spawn<string | null>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<DatePickerDialog
				{...forward}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve(null)}
				onSubmitAsync={async (date, opts) => {
					await onSubmitAsync?.(date, opts);
					resolve(date);
				}}
			/>
		)),
	).promise;
}
